// src/components/EditPlace.js

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  LuArrowLeft,
  LuBuilding2,
  LuChevronDown,
  LuChevronUp,
  LuClock3,
  LuExternalLink,
  LuLink2,
  LuLocateFixed,
  LuMapPin,
  LuPlus,
  LuX,
} from "react-icons/lu";

import Select from "react-select";

import maplibregl from "maplibre-gl";

import axios from "../axiosConfig";

import {
  readPlaceCache,
  writePlaceCache,
} from "../lib/placeEditCache";

import {
  useTranslation,
} from "react-i18next";

import "maplibre-gl/dist/maplibre-gl.css";
import "../styles/EditPlace.css";

/* =========================================================
   General constants
   ========================================================= */

const DAYS = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];

const PLACE_CATEGORY_VALUES = [
  "INDUSTRY",
  "WAREHOUSE",
  "GAS_STATION",
  "PORT",
  "AIRPORT",
  "CLIENT",
  "SUPPLIER",
  "PARKING",
  "REST_AREA",
  "CUSTOMS",
  "WORKSHOP",
  "CONSTRUCTION_SITE",
  "OTHER",
];

const countryOptions = [
  {
    label: "Austria",
    value: "Austria",
    code: "at",
    dialCode: "+43",
  },
  {
    label: "Belgium",
    value: "Belgium",
    code: "be",
    dialCode: "+32",
  },
  {
    label: "Croatia",
    value: "Croatia",
    code: "hr",
    dialCode: "+385",
  },
  {
    label: "Czech Republic",
    value: "Czech Republic",
    code: "cz",
    dialCode: "+420",
  },
  {
    label: "Denmark",
    value: "Denmark",
    code: "dk",
    dialCode: "+45",
  },
  {
    label: "Estonia",
    value: "Estonia",
    code: "ee",
    dialCode: "+372",
  },
  {
    label: "Finland",
    value: "Finland",
    code: "fi",
    dialCode: "+358",
  },
  {
    label: "France",
    value: "France",
    code: "fr",
    dialCode: "+33",
  },
  {
    label: "Germany",
    value: "Germany",
    code: "de",
    dialCode: "+49",
  },
  {
    label: "Hungary",
    value: "Hungary",
    code: "hu",
    dialCode: "+36",
  },
  {
    label: "Italy",
    value: "Italy",
    code: "it",
    dialCode: "+39",
  },
  {
    label: "Netherlands",
    value: "Netherlands",
    code: "nl",
    dialCode: "+31",
  },
  {
    label: "Norway",
    value: "Norway",
    code: "no",
    dialCode: "+47",
  },
  {
    label: "Poland",
    value: "Poland",
    code: "pl",
    dialCode: "+48",
  },
  {
    label: "Portugal",
    value: "Portugal",
    code: "pt",
    dialCode: "+351",
  },
  {
    label: "Spain",
    value: "Spain",
    code: "es",
    dialCode: "+34",
  },
  {
    label: "Sweden",
    value: "Sweden",
    code: "se",
    dialCode: "+46",
  },
  {
    label: "Switzerland",
    value: "Switzerland",
    code: "ch",
    dialCode: "+41",
  },
  {
    label: "United Kingdom",
    value: "United Kingdom",
    code: "gb",
    dialCode: "+44",
  },
];

const DEFAULT_PHONE_COUNTRY =
  countryOptions.find(
    (country) =>
      country.value ===
      "Portugal"
  ) ||
  countryOptions[0];

/* =========================================================
   Working-hours helpers
   ========================================================= */

function createEmptyWorkingHours() {
  return {
    Mon: [],
    Tue: [],
    Wed: [],
    Thu: [],
    Fri: [],
    Sat: [],
    Sun: [],
  };
}

function normalizeWorkingHours(
  workingHours
) {
  if (
    !workingHours
  ) {
    return createEmptyWorkingHours();
  }

  let parsed =
    workingHours;

  if (
    typeof workingHours ===
    "string"
  ) {
    try {
      parsed =
        JSON.parse(
          workingHours
        );
    } catch {
      parsed =
        {};
    }
  }

  return {
    ...createEmptyWorkingHours(),
    ...(
      parsed &&
      typeof parsed ===
        "object"
        ? parsed
        : {}
    ),
  };
}

function createEmptyPlaceForm() {
  return {
    placeName: "",
    category: "OTHER",
    contactPhone: "",
    address: "",
    city: "",
    country: "",
    postalCode: "",
    gMapsLink: "",
    coordinates: "",
    workingHours:
      createEmptyWorkingHours(),
  };
}

function normalizePlaceData(
  source = {}
) {
  return {
    placeName:
      source.placeName ||
      "",

    category:
      source.category ||
      "OTHER",

    contactPhone:
      source.contactPhone ===
        null ||
      source.contactPhone ===
        undefined
        ? ""
        : String(
            source.contactPhone
          ),

    address:
      source.address ||
      "",

    city:
      source.city ||
      "",

    country:
      source.country ||
      "",

    postalCode:
      source.postalCode ||
      "",

    gMapsLink:
      source.gMapsLink ||
      "",

    coordinates:
      source.coordinates ||
      "",

    workingHours:
      normalizeWorkingHours(
        source.workingHours
      ),
  };
}

function isSamePlaceForm(
  current,
  incoming
) {
  if (
    !current ||
    !incoming
  ) {
    return false;
  }

  if (
    current.placeName !==
    incoming.placeName
  ) {
    return false;
  }

  if (
    current.category !==
    incoming.category
  ) {
    return false;
  }

  if (
    current.contactPhone !==
    incoming.contactPhone
  ) {
    return false;
  }

  if (
    current.address !==
    incoming.address
  ) {
    return false;
  }

  if (
    current.city !==
    incoming.city
  ) {
    return false;
  }

  if (
    current.country !==
    incoming.country
  ) {
    return false;
  }

  if (
    current.postalCode !==
    incoming.postalCode
  ) {
    return false;
  }

  if (
    current.gMapsLink !==
    incoming.gMapsLink
  ) {
    return false;
  }

  if (
    current.coordinates !==
    incoming.coordinates
  ) {
    return false;
  }

  try {
    return (
      JSON.stringify(
        normalizeWorkingHours(
          current.workingHours
        )
      ) ===
      JSON.stringify(
        normalizeWorkingHours(
          incoming.workingHours
        )
      )
    );
  } catch {
    return false;
  }
}

/* =========================================================
   Time picker
   ========================================================= */

function buildTimes(
  stepMinutes = 15
) {
  const output = [];

  for (
    let hour = 0;
    hour < 24;
    hour += 1
  ) {
    for (
      let minute = 0;
      minute < 60;
      minute += stepMinutes
    ) {
      const formattedHour =
        String(
          hour
        ).padStart(
          2,
          "0"
        );

      const formattedMinute =
        String(
          minute
        ).padStart(
          2,
          "0"
        );

      output.push(
        `${formattedHour}:${formattedMinute}`
      );
    }
  }

  return output;
}

const TIME_OPTIONS =
  buildTimes(
    15
  );

function normalizeTimeValue(
  value
) {
  const raw =
    String(
      value ||
        ""
    ).trim();

  if (
    !raw
  ) {
    return "";
  }

  let hours = 0;
  let minutes = 0;

  if (
    raw.includes(
      ":"
    )
  ) {
    const [
      hourPart,
      minutePart = "",
    ] =
      raw.split(
        ":"
      );

    hours =
      Number.parseInt(
        hourPart ||
          "0",
        10
      );

    minutes =
      Number.parseInt(
        minutePart.padEnd(
          2,
          "0"
        ) ||
          "0",
        10
      );
  } else {
    const digits =
      raw
        .replace(
          /\D/g,
          ""
        )
        .slice(
          0,
          4
        );

    if (
      digits.length <=
      2
    ) {
      hours =
        Number.parseInt(
          digits ||
            "0",
          10
        );

      minutes = 0;
    } else {
      hours =
        Number.parseInt(
          digits.slice(
            0,
            2
          ),
          10
        );

      minutes =
        Number.parseInt(
          digits
            .slice(
              2,
              4
            )
            .padEnd(
              2,
              "0"
            ) ||
            "0",
          10
        );
    }
  }

  const safeHours =
    Math.max(
      0,
      Math.min(
        23,
        Number.isNaN(
          hours
        )
          ? 0
          : hours
      )
    );

  const safeMinutes =
    Math.max(
      0,
      Math.min(
        59,
        Number.isNaN(
          minutes
        )
          ? 0
          : minutes
      )
    );

  return `${String(
    safeHours
  ).padStart(
    2,
    "0"
  )}:${String(
    safeMinutes
  ).padStart(
    2,
    "0"
  )}`;
}

function TimePicker({
  value,
  onChange,
  align = "left",
  placeholder = "hh:mm",
  isError = false,
}) {
  const [
    open,
    setOpen,
  ] =
    useState(
      false
    );

  const rootRef =
    useRef(
      null
    );

  useEffect(
    () => {
      const handleOutsideClick = (
        event
      ) => {
        if (
          rootRef.current &&
          !rootRef.current.contains(
            event.target
          )
        ) {
          setOpen(
            false
          );
        }
      };

      document.addEventListener(
        "mousedown",
        handleOutsideClick
      );

      return () => {
        document.removeEventListener(
          "mousedown",
          handleOutsideClick
        );
      };
    },
    []
  );

  return (
    <div
      ref={
        rootRef
      }
      className={`edit-place-tp ${
        align ===
        "right"
          ? "edit-place-tp--right"
          : ""
      }`}
    >
      <input
        type="text"
        value={
          value ||
          ""
        }
        placeholder={
          placeholder
        }
        inputMode="numeric"
        maxLength={5}
        className={`edit-place-input-time ${
          isError
            ? "edit-place-input-error"
            : ""
        }`}
        onFocus={() =>
          setOpen(
            true
          )
        }
        onClick={() =>
          setOpen(
            true
          )
        }
        onChange={(
          event
        ) => {
          const raw =
            event.target.value ||
            "";

          if (
            !raw
          ) {
            onChange(
              ""
            );

            return;
          }

          const digits =
            raw
              .replace(
                /\D/g,
                ""
              )
              .slice(
                0,
                4
              );

          if (
            digits.length <=
            2
          ) {
            onChange(
              digits
            );

            return;
          }

          onChange(
            `${digits.slice(
              0,
              2
            )}:${digits.slice(
              2
            )}`
          );
        }}
        onBlur={() => {
          if (
            value
          ) {
            onChange(
              normalizeTimeValue(
                value
              )
            );
          }
        }}
      />

      {open && (
        <div className="edit-place-tp-menu">
          {TIME_OPTIONS.map(
            (
              time
            ) => (
              <button
                key={
                  time
                }
                type="button"
                className="edit-place-tp-item"
                onMouseDown={(
                  event
                ) => {
                  event.preventDefault();

                  onChange(
                    time
                  );

                  setOpen(
                    false
                  );
                }}
              >
                {time}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   React Select styles
   ========================================================= */

function createSelectStyles(
  hasError = false
) {
  return {
    control: (
      base,
      state
    ) => ({
      ...base,

      width:
        "100%",

      minHeight:
        44,

      height:
        44,

      backgroundColor:
        hasError
          ? "#3a0000"
          : "#15171b",

      border:
        `1px solid ${
          hasError
            ? "#e15252"
            : state.isFocused
            ? "#2f8fff"
            : "rgba(255,255,255,0.12)"
        }`,

      borderRadius:
        10,

      boxShadow:
        state.isFocused
          ? hasError
            ? "0 0 0 3px rgba(225,82,82,0.14)"
            : "0 0 0 3px rgba(47,143,255,0.14)"
          : "none",

      cursor:
        "pointer",

      transition:
        "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",

      "&:hover": {
        borderColor:
          hasError
            ? "#e15252"
            : "#2f8fff",
      },
    }),

    valueContainer: (
      base
    ) => ({
      ...base,

      height:
        44,

      padding:
        "0 13px",
    }),

    input: (
      base
    ) => ({
      ...base,

      color:
        "#f5f7fb",

      margin:
        0,

      padding:
        0,
    }),

    singleValue: (
      base
    ) => ({
      ...base,

      color:
        "#f5f7fb",

      fontSize:
        13,

      fontWeight:
        600,
    }),

    placeholder: (
      base
    ) => ({
      ...base,

      color:
        "#747d8e",

      fontSize:
        13,
    }),

    indicatorsContainer: (
      base
    ) => ({
      ...base,

      height:
        44,
    }),

    indicatorSeparator: (
      base
    ) => ({
      ...base,

      backgroundColor:
        "rgba(255,255,255,0.09)",
    }),

    dropdownIndicator: (
      base,
      state
    ) => ({
      ...base,

      color:
        state.isFocused
          ? "#5aa7ff"
          : "#8e98aa",

      padding:
        9,

      "&:hover": {
        color:
          "#ffffff",
      },
    }),

    clearIndicator: (
      base
    ) => ({
      ...base,

      color:
        "#8993a5",

      padding:
        8,

      "&:hover": {
        color:
          "#ffffff",
      },
    }),

    menuPortal: (
      base
    ) => ({
      ...base,

      zIndex:
        99999,
    }),

    menu: (
      base
    ) => ({
      ...base,

      overflow:
        "hidden",

      marginTop:
        6,

      padding:
        0,

      background:
        "#111317",

      border:
        "1px solid rgba(255,255,255,0.12)",

      borderRadius:
        12,

      boxShadow:
        "0 22px 55px rgba(0,0,0,0.52)",
    }),

    menuList: (
      base
    ) => ({
      ...base,

      maxHeight:
        260,

      padding:
        6,
    }),

    option: (
      base,
      state
    ) => ({
      ...base,

      padding:
        "10px 12px",

      borderRadius:
        8,

      backgroundColor:
        state.isSelected
          ? "rgba(47,143,255,0.24)"
          : state.isFocused
          ? "rgba(47,143,255,0.12)"
          : "transparent",

      color:
        state.isSelected
          ? "#ffffff"
          : "#dce3ef",

      cursor:
        "pointer",

      fontSize:
        13,

      "&:active": {
        backgroundColor:
          "rgba(47,143,255,0.20)",
      },
    }),

    noOptionsMessage: (
      base
    ) => ({
      ...base,

      color:
        "#919bad",

      fontSize:
        13,
    }),
  };
}

const phoneSelectStyles = {
  control: (
    base
  ) => ({
    ...base,

    minHeight:
      42,

    height:
      42,

    background:
      "transparent",

    border:
      "none",

    boxShadow:
      "none",

    cursor:
      "pointer",
  }),

  valueContainer: (
    base
  ) => ({
    ...base,

    height:
      42,

    padding:
      "0 7px 0 11px",

    display:
      "flex",

    alignItems:
      "center",

    overflow:
      "visible",
  }),

  singleValue: (
    base
  ) => ({
    ...base,

    position:
      "static",

    transform:
      "none",

    margin:
      0,

    maxWidth:
      "none",

    overflow:
      "visible",

    display:
      "flex",

    alignItems:
      "center",

    color:
      "#f5f7fb",
  }),

  input: (
    base
  ) => ({
    ...base,

    color:
      "#f5f7fb",

    margin:
      0,

    padding:
      0,
  }),

  placeholder: (
    base
  ) => ({
    ...base,

    color:
      "#747d8e",

    margin:
      0,
  }),

  indicatorsContainer: (
    base
  ) => ({
    ...base,

    height:
      42,

    alignItems:
      "center",
  }),

  dropdownIndicator: (
    base
  ) => ({
    ...base,

    color:
      "#8e98aa",

    padding:
      5,

    "&:hover": {
      color:
        "#ffffff",
    },
  }),

  indicatorSeparator: () => ({
    display:
      "none",
  }),

  menuPortal: (
    base
  ) => ({
    ...base,

    zIndex:
      99999,
  }),

  menu: (
    base
  ) => ({
    ...base,

    width:
      280,

    minWidth:
      280,

    overflow:
      "hidden",

    background:
      "#111317",

    border:
      "1px solid rgba(255,255,255,0.12)",

    borderRadius:
      12,

    boxShadow:
      "0 22px 55px rgba(0,0,0,0.52)",
  }),

  menuList: (
    base
  ) => ({
    ...base,

    maxHeight:
      270,

    padding:
      6,
  }),

  option: (
    base,
    state
  ) => ({
    ...base,

    borderRadius:
      8,

    padding:
      "10px 12px",

    background:
      state.isSelected
        ? "rgba(47,143,255,0.24)"
        : state.isFocused
        ? "rgba(47,143,255,0.12)"
        : "transparent",

    color:
      "#f5f7fb",

    cursor:
      "pointer",
  }),
};

/* =========================================================
   Small UI components
   ========================================================= */

function SectionHeading({
  icon: Icon,
  title,
  description,
}) {
  return (
    <div className="edit-place-section-heading">
      <div className="edit-place-section-icon">
        <Icon />
      </div>

      <div className="edit-place-section-copy">
        <h2>
          {title}
        </h2>

        {description && (
          <p>
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

function FieldError({
  show,
  children,
  dropdown = false,
}) {
  return (
    <span
      className={
        dropdown
          ? "edit-place-dropdown-field-error"
          : "edit-place-field-error"
      }
    >
      {show
        ? children
        : "\u00A0"}
    </span>
  );
}

/* =========================================================
   Map preview
   ========================================================= */

const EDIT_PLACE_MAP_STYLE =
  "/maps/styles/loadevo-road.json?v=5";

const EDIT_PLACE_MAP_DEFAULT_CENTER = [
  -8.2245,
  39.3999,
];

function parseCoordinatesInput(
  value
) {
  const raw =
    String(
      value ||
        ""
    )
      .trim()
      .replace(
        /[\[\]()]/g,
        ""
      );

  if (
    !raw
  ) {
    return null;
  }

  const separator =
    raw.includes(
      ";"
    )
      ? ";"
      : ",";

  const parts =
    raw
      .split(
        separator
      )
      .map(
        (part) =>
          part.trim()
      )
      .filter(
        Boolean
      );

  if (
    parts.length !==
    2
  ) {
    return null;
  }

  const latitude =
    Number(
      parts[0].replace(
        ",",
        "."
      )
    );

  const longitude =
    Number(
      parts[1].replace(
        ",",
        "."
      )
    );

  if (
    !Number.isFinite(
      latitude
    ) ||
    !Number.isFinite(
      longitude
    ) ||
    latitude <
      -90 ||
    latitude >
      90 ||
    longitude <
      -180 ||
    longitude >
      180
  ) {
    return null;
  }

  return {
    latitude,
    longitude,
  };
}

function formatCoordinatesLabel(
  coordinates
) {
  if (
    !coordinates
  ) {
    return "";
  }

  return `${coordinates.latitude.toFixed(
    6
  )}, ${coordinates.longitude.toFixed(
    6
  )}`;
}

function EditPlaceMapPreview({
  value,
  emptyLabel,
  invalidLabel,
  previewLabel,
  openMapLabel,
}) {
  const mapContainerRef =
    useRef(
      null
    );

  const mapRef =
    useRef(
      null
    );

  const markerRef =
    useRef(
      null
    );

  const parsedCoordinates =
    useMemo(
      () =>
        parseCoordinatesInput(
          value
        ),
      [
        value,
      ]
    );

  const hasCoordinateValue =
    Boolean(
      String(
        value ||
          ""
      ).trim()
    );

  const coordinateLabel =
    parsedCoordinates
      ? formatCoordinatesLabel(
          parsedCoordinates
        )
      : hasCoordinateValue
      ? invalidLabel
      : emptyLabel;

  const googleMapsHref =
    parsedCoordinates
      ? `https://www.google.com/maps?q=${encodeURIComponent(
          `${parsedCoordinates.latitude},${parsedCoordinates.longitude}`
        )}`
      : "";

  useEffect(
    () => {
      if (
        !mapContainerRef.current ||
        mapRef.current
      ) {
        return undefined;
      }

      const map =
        new maplibregl.Map(
          {
            container:
              mapContainerRef.current,

            style:
              EDIT_PLACE_MAP_STYLE,

            center:
              EDIT_PLACE_MAP_DEFAULT_CENTER,

            zoom:
              5.3,

            attributionControl:
              false,

            interactive:
              true,
          }
        );

      map.addControl(
        new maplibregl.AttributionControl(
          {
            compact:
              true,
          }
        ),
        "bottom-right"
      );

      mapRef.current =
        map;

      const resizeTimer =
        window.setTimeout(
          () => {
            try {
              map.resize();
            } catch {}
          },
          80
        );

      return () => {
        window.clearTimeout(
          resizeTimer
        );

        if (
          markerRef.current
        ) {
          markerRef.current.remove();

          markerRef.current =
            null;
        }

        try {
          map.remove();
        } catch {}

        mapRef.current =
          null;
      };
    },
    []
  );

  useEffect(
    () => {
      const map =
        mapRef.current;

      if (
        !map
      ) {
        return undefined;
      }

      const updateMap =
        () => {
          if (
            !parsedCoordinates
          ) {
            if (
              markerRef.current
            ) {
              markerRef.current.remove();

              markerRef.current =
                null;
            }

            map.easeTo(
              {
                center:
                  EDIT_PLACE_MAP_DEFAULT_CENTER,

                zoom:
                  5.3,

                duration:
                  350,
              }
            );

            return;
          }

          const center = [
            parsedCoordinates.longitude,
            parsedCoordinates.latitude,
          ];

          if (
            !markerRef.current
          ) {
            markerRef.current =
              new maplibregl.Marker(
                {
                  color:
                    "#2f8fff",

                  scale:
                    0.86,
                }
              )
                .setLngLat(
                  center
                )
                .addTo(
                  map
                );
          } else {
            markerRef.current.setLngLat(
              center
            );
          }

          map.easeTo(
            {
              center,

              zoom:
                14.5,

              duration:
                450,
            }
          );
        };

      if (
        map.loaded()
      ) {
        updateMap();
      } else {
        map.once(
          "load",
          updateMap
        );
      }

      return () => {
        try {
          map.off(
            "load",
            updateMap
          );
        } catch {}
      };
    },
    [
      parsedCoordinates,
    ]
  );

  return (
    <aside className="edit-place-map-preview">
      <div className="edit-place-map-canvas-wrap">
        <div
          ref={
            mapContainerRef
          }
          className="edit-place-map-canvas"
        />

        {!parsedCoordinates && (
          <div
            className={`edit-place-map-empty-state ${
              hasCoordinateValue
                ? "is-invalid"
                : ""
            }`}
          >
            <div className="edit-place-map-empty-icon">
              <LuMapPin />
            </div>

            <span>
              {hasCoordinateValue
                ? invalidLabel
                : emptyLabel}
            </span>
          </div>
        )}
      </div>

      <div className="edit-place-map-caption">
        <div className="edit-place-map-caption-icon">
          <LuLocateFixed />
        </div>

        <div className="edit-place-map-caption-copy">
          <strong
            title={
              coordinateLabel
            }
          >
            {coordinateLabel}
          </strong>

          <span>
            {previewLabel}
          </span>
        </div>

        {parsedCoordinates && (
          <a
            className="edit-place-map-open-link"
            href={
              googleMapsHref
            }
            target="_blank"
            rel="noreferrer"
          >
            <span>
              {openMapLabel}
            </span>

            <LuExternalLink />
          </a>
        )}
      </div>
    </aside>
  );
}

/* =========================================================
   Page
   ========================================================= */

function EditPlace() {
  const {
    t,
  } =
    useTranslation(
      "edit_place"
    );

  const {
    placeId,
  } =
    useParams();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const menuPortalTarget =
    typeof document !==
    "undefined"
      ? document.body
      : null;

  const placeCategoryOptions =
    useMemo(
      () =>
        PLACE_CATEGORY_VALUES.map(
          (
            value
          ) => ({
            value,

            label:
              t(
                `categories.${value}`,
                {
                  defaultValue:
                    value,
                }
              ),
          })
        ),
      [
        t,
      ]
    );

  const normalizedPrefill =
    useMemo(
      () => {
        const prefill =
          location.state?.prefill;

        return prefill
          ? normalizePlaceData(
              prefill
            )
          : null;
      },
      [
        location.state,
      ]
    );

  const [
    cacheInit,
  ] =
    useState(
      () => {
        if (
          normalizedPrefill
        ) {
          return null;
        }

        return readPlaceCache(
          placeId
        );
      }
    );

  const normalizedCache =
    useMemo(
      () =>
        cacheInit
          ? normalizePlaceData(
              cacheInit
            )
          : null,
      [
        cacheInit,
      ]
    );

  const initialSeed =
    normalizedPrefill ||
    normalizedCache;

  const [
    formData,
    setFormData,
  ] =
    useState(
      () => ({
        ...createEmptyPlaceForm(),
        ...(
          normalizedCache ||
          {}
        ),
        ...(
          normalizedPrefill ||
          {}
        ),
      })
    );

  const [
    selectedCountry,
    setSelectedCountry,
  ] =
    useState(
      () => {
        const initialCountry =
          initialSeed?.country ||
          "";

        return (
          countryOptions.find(
            (country) =>
              country.value ===
                initialCountry ||
              country.label ===
                initialCountry
          ) ||
          DEFAULT_PHONE_COUNTRY
        );
      }
    );

  const hasInstantData =
    Boolean(
      normalizedPrefill ||
      normalizedCache
    );

  const [
    bootLoading,
    setBootLoading,
  ] =
    useState(
      () =>
        !hasInstantData
    );

  const [
    invalidFields,
    setInvalidFields,
  ] =
    useState(
      {}
    );

  const [
    workingHoursErrors,
    setWorkingHoursErrors,
  ] =
    useState(
      {}
    );

  const [
    showWorkingHours,
    setShowWorkingHours,
  ] =
    useState(
      false
    );

  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState(
      ""
    );

  const [
    showSuccessPopup,
    setShowSuccessPopup,
  ] =
    useState(
      false
    );

  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(
      false
    );

  const formDirtyRef =
    useRef(
      false
    );

  const selectedCategory =
    placeCategoryOptions.find(
      (option) =>
        option.value ===
        formData.category
    ) ||
    null;

  const selectedPlaceCountry =
    countryOptions.find(
      (option) =>
        option.value ===
          formData.country ||
        option.label ===
          formData.country
    ) ||
    null;

  /* ---------------------------------------------------------
     Fetch current place
     --------------------------------------------------------- */

  useEffect(
    () => {
      let alive =
        true;

      const fetchPlaceDetails =
        async () => {
          try {
            if (
              !hasInstantData
            ) {
              setBootLoading(
                true
              );
            }

            const {
              data,
            } =
              await axios.get(
                `/places/${placeId}`
              );

            if (
              !alive
            ) {
              return;
            }

            const incoming =
              normalizePlaceData(
                data
              );

            writePlaceCache(
              placeId,
              incoming
            );

            if (
              !formDirtyRef.current
            ) {
              setFormData(
                (
                  previous
                ) => {
                  const merged = {
                    ...previous,
                    ...incoming,
                  };

                  return isSamePlaceForm(
                    previous,
                    merged
                  )
                    ? previous
                    : merged;
                }
              );

              const detectedCountry =
                countryOptions.find(
                  (country) =>
                    country.value ===
                      incoming.country ||
                    country.label ===
                      incoming.country
                );

              if (
                detectedCountry
              ) {
                setSelectedCountry(
                  detectedCountry
                );
              }
            }
          } catch (
            error
          ) {
            if (
              !alive
            ) {
              return;
            }

            console.error(
              "[EditPlace] fetch failed:",
              error
            );

            setErrorMessage(
              t(
                "errors.fetchFailed"
              )
            );
          } finally {
            if (
              alive
            ) {
              setBootLoading(
                false
              );
            }
          }
        };

      fetchPlaceDetails();

      return () => {
        alive =
          false;
      };
    },
    [
      hasInstantData,
      placeId,
      t,
    ]
  );

  useEffect(
    () => {
      if (
        !normalizedPrefill
      ) {
        return;
      }

      writePlaceCache(
        placeId,
        normalizedPrefill
      );
    },
    [
      normalizedPrefill,
      placeId,
    ]
  );

  /* ---------------------------------------------------------
     Generic handlers
     --------------------------------------------------------- */

  const clearFieldError = (
    field
  ) => {
    setInvalidFields(
      (
        previous
      ) => {
        if (
          !previous[field]
        ) {
          return previous;
        }

        const next = {
          ...previous,
        };

        delete next[field];

        return next;
      }
    );
  };

  const markFormAsDirty =
    () => {
      formDirtyRef.current =
        true;
    };

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
    } =
      event.target;

    markFormAsDirty();

    setFormData(
      (
        previous
      ) => ({
        ...previous,
        [name]:
          value,
      })
    );

    clearFieldError(
      name
    );
  };

  const handleCategoryChange = (
    selectedOption
  ) => {
    markFormAsDirty();

    setFormData(
      (
        previous
      ) => ({
        ...previous,

        category:
          selectedOption?.value ||
          "",
      })
    );

    clearFieldError(
      "category"
    );
  };

  const handleCountryChange = (
    selectedOption
  ) => {
    markFormAsDirty();

    setFormData(
      (
        previous
      ) => ({
        ...previous,

        country:
          selectedOption?.value ||
          "",
      })
    );

    clearFieldError(
      "country"
    );
  };

  const handleCountryPhoneChange = (
    selectedOption
  ) => {
    if (
      selectedOption
    ) {
      markFormAsDirty();

      setSelectedCountry(
        selectedOption
      );
    }
  };

  const handleContactPhoneChange = (
    event
  ) => {
    const value =
      event.target.value.replace(
        /[^\d]/g,
        ""
      );

    markFormAsDirty();

    setFormData(
      (
        previous
      ) => ({
        ...previous,

        contactPhone:
          value,
      })
    );

    clearFieldError(
      "contactPhone"
    );
  };

  /* ---------------------------------------------------------
     Working hours
     --------------------------------------------------------- */

  const addInterval = (
    day
  ) => {
    markFormAsDirty();

    setFormData(
      (
        previous
      ) => ({
        ...previous,

        workingHours: {
          ...previous.workingHours,

          [day]: [
            ...(
              previous.workingHours[
                day
              ] ||
              []
            ),

            [
              "",
              "",
            ],
          ],
        },
      })
    );

    setWorkingHoursErrors(
      (
        previous
      ) => ({
        ...previous,

        [day]:
          undefined,
      })
    );
  };

  const updateInterval = (
    day,
    index,
    position,
    value
  ) => {
    markFormAsDirty();

    setFormData(
      (
        previous
      ) => {
        const intervals =
          (
            previous.workingHours[
              day
            ] ||
            []
          ).map(
            (
              interval
            ) => [
              ...interval,
            ]
          );

        if (
          !intervals[index]
        ) {
          return previous;
        }

        intervals[
          index
        ][position] =
          value;

        return {
          ...previous,

          workingHours: {
            ...previous.workingHours,

            [day]:
              intervals,
          },
        };
      }
    );

    setWorkingHoursErrors(
      (
        previous
      ) => ({
        ...previous,

        [day]:
          undefined,
      })
    );
  };

  const removeInterval = (
    day,
    index
  ) => {
    markFormAsDirty();

    setFormData(
      (
        previous
      ) => {
        const intervals = [
          ...(
            previous.workingHours[
              day
            ] ||
            []
          ),
        ];

        intervals.splice(
          index,
          1
        );

        return {
          ...previous,

          workingHours: {
            ...previous.workingHours,

            [day]:
              intervals,
          },
        };
      }
    );

    setWorkingHoursErrors(
      (
        previous
      ) => ({
        ...previous,

        [day]:
          undefined,
      })
    );
  };

  const copyIntervals = (
    intervals
  ) =>
    intervals.map(
      (
        interval
      ) => [
        interval[0] ||
          "",
        interval[1] ||
          "",
      ]
    );

  const applyWorkingHoursToDays = (
    sourceDay,
    targetDays
  ) => {
    markFormAsDirty();

    setFormData(
      (
        previous
      ) => {
        const sourceIntervals =
          previous.workingHours[
            sourceDay
          ] ||
          [];

        if (
          sourceIntervals.length ===
          0
        ) {
          return previous;
        }

        const nextWorkingHours = {
          ...previous.workingHours,
        };

        targetDays.forEach(
          (
            targetDay
          ) => {
            nextWorkingHours[
              targetDay
            ] =
              copyIntervals(
                sourceIntervals
              );
          }
        );

        return {
          ...previous,

          workingHours:
            nextWorkingHours,
        };
      }
    );

    setWorkingHoursErrors(
      (
        previous
      ) => {
        const next = {
          ...previous,
        };

        targetDays.forEach(
          (
            targetDay
          ) => {
            delete next[
              targetDay
            ];
          }
        );

        return next;
      }
    );
  };

  const applyWorkingHoursToWeekdays = (
    sourceDay
  ) => {
    applyWorkingHoursToDays(
      sourceDay,
      [
        "Mon",
        "Tue",
        "Wed",
        "Thu",
        "Fri",
      ]
    );
  };

  const applyWorkingHoursToAllDays = (
    sourceDay
  ) => {
    applyWorkingHoursToDays(
      sourceDay,
      DAYS
    );
  };

  const validateWorkingHoursLocal = (
    workingHours
  ) => {
    const errors = {};

    DAYS.forEach(
      (
        day
      ) => {
        const intervals =
          workingHours[
            day
          ] ||
          [];

        for (
          let index = 0;
          index <
          intervals.length;
          index += 1
        ) {
          const [
            start,
            end,
          ] =
            intervals[
              index
            ];

          if (
            !start ||
            !end
          ) {
            errors[
              day
            ] =
              t(
                "errors.workingHoursFillBoth"
              );

            break;
          }

          if (
            end <=
            start
          ) {
            errors[
              day
            ] =
              t(
                "errors.workingHoursEndAfterStart"
              );

            break;
          }
        }
      }
    );

    setWorkingHoursErrors(
      errors
    );

    return (
      Object.keys(
        errors
      ).length ===
      0
    );
  };

  /* ---------------------------------------------------------
     Validation and submit
     --------------------------------------------------------- */

  const validateFields =
    () => {
      const errors = {};

      if (
        !formData.placeName.trim()
      ) {
        errors.placeName =
          true;
      }

      if (
        !formData.category
      ) {
        errors.category =
          true;
      }

      if (
        !formData.contactPhone.trim()
      ) {
        errors.contactPhone =
          true;
      }

      if (
        !formData.address.trim()
      ) {
        errors.address =
          true;
      }

      if (
        !formData.postalCode.trim()
      ) {
        errors.postalCode =
          true;
      }

      if (
        !formData.city.trim()
      ) {
        errors.city =
          true;
      }

      if (
        !formData.country.trim()
      ) {
        errors.country =
          true;
      }

      if (
        !formData.gMapsLink.trim()
      ) {
        errors.gMapsLink =
          true;
      }

      if (
        !formData.coordinates.trim()
      ) {
        errors.coordinates =
          "required";
      } else if (
        !parseCoordinatesInput(
          formData.coordinates
        )
      ) {
        errors.coordinates =
          "invalid";
      }

      return errors;
    };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (
      isSubmitting
    ) {
      return;
    }

    setErrorMessage(
      ""
    );

    const fieldErrors =
      validateFields();

    setInvalidFields(
      fieldErrors
    );

    if (
      Object.keys(
        fieldErrors
      ).length >
      0
    ) {
      return;
    }

    const hasAnyInterval =
      DAYS.some(
        (
          day
        ) =>
          (
            formData.workingHours[
              day
            ] ||
            []
          ).length >
          0
      );

    if (
      hasAnyInterval &&
      !validateWorkingHoursLocal(
        formData.workingHours
      )
    ) {
      return;
    }

    const payload = {
      ...formData,

      workingHours:
        normalizeWorkingHours(
          formData.workingHours
        ),
    };

    setIsSubmitting(
      true
    );

    try {
      await axios.put(
        `/places/${placeId}`,
        payload
      );

      writePlaceCache(
        placeId,
        payload
      );

      formDirtyRef.current =
        false;

      if (
        typeof window !==
        "undefined"
      ) {
        window.dispatchEvent(
          new CustomEvent(
            "loadevo:place-updated",
            {
              detail: {
                placeId,
                place:
                  payload,
              },
            }
          )
        );
      }

      setShowSuccessPopup(
        true
      );

      window.setTimeout(
        () => {
          navigate(
            "/places"
          );
        },
        1200
      );
    } catch (
      error
    ) {
      console.error(
        "[EditPlace] update failed:",
        error
      );

      setShowSuccessPopup(
        false
      );

      setErrorMessage(
        error.response
          ?.data
          ?.error ||
          t(
            "errors.updateFailed"
          )
      );
    } finally {
      setIsSubmitting(
        false
      );
    }
  };

  const handleCancel =
    () => {
      navigate(
        "/places"
      );
    };

  return (
    <div className="edit-place-container">
      <header className="edit-place-page-header">
        <button
          type="button"
          className="edit-place-back-button"
          onClick={
            handleCancel
          }
        >
          <LuArrowLeft className="edit-place-back-button__icon" />

          <span className="edit-place-back-button__label">
            {t(
              "page.back"
            )}
          </span>
        </button>

        <div className="edit-place-page-heading">
          <h1 className="edit-place-title">
            {t(
              "page.title"
            )}
          </h1>

          <p className="edit-place-subtitle">
            {t(
              "page.subtitle"
            )}
          </p>
        </div>
      </header>

      {errorMessage && (
        <div
          className="edit-place-error-message"
          role="alert"
        >
          {errorMessage}
        </div>
      )}

      {showSuccessPopup && (
        <div className="edit-place-successPopup-overlay">
          <div
            className="edit-place-successPopup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-place-success-title"
          >
            <div className="edit-place-successPopup-icon">
              ✓
            </div>

            <h3 id="edit-place-success-title">
              {t(
                "success.savedTitle"
              )}
            </h3>

            <p>
              {t(
                "success.saved"
              )}
            </p>

            <button
              type="button"
              className="edit-place-successPopup-btn"
              onClick={
                handleCancel
              }
            >
              {t(
                "actions.ok"
              )}
            </button>
          </div>
        </div>
      )}

      {bootLoading ? (
        <div className="edit-place-boot-loading">
          <div className="edit-place-boot-loading__spinner" />

          <span>
            {t(
              "common.loading"
            )}
          </span>
        </div>
      ) : (
        <form
          className="edit-place-form"
          onSubmit={
            handleSubmit
          }
          noValidate
        >
          {/* =================================================
              Basic information
              ================================================= */}

          <section className="edit-place-section">
            <SectionHeading
              icon={
                LuBuilding2
              }
              title={t(
                "sections.basic"
              )}
              description={t(
                "sections.basicDescription"
              )}
            />

            <div className="edit-place-basic-grid">
              <div className="edit-place-input-group">
                <label
                  className="edit-place-label"
                  htmlFor="placeName"
                >
                  {t(
                    "fields.placeName"
                  )}
                  *
                </label>

                <input
                  type="text"
                  id="placeName"
                  name="placeName"
                  value={
                    formData.placeName
                  }
                  placeholder={t(
                    "placeholders.placeName"
                  )}
                  className={
                    invalidFields.placeName
                      ? "edit-place-input-error"
                      : ""
                  }
                  onChange={
                    handleChange
                  }
                />

                <FieldError
                  show={
                    invalidFields.placeName
                  }
                >
                  {t(
                    "errors.placeNameRequired"
                  )}
                </FieldError>
              </div>

              <div className="edit-place-input-group">
                <label
                  className="edit-place-label"
                  htmlFor="category"
                >
                  {t(
                    "fields.category"
                  )}
                  *
                </label>

                <Select
                  inputId="category"
                  value={
                    selectedCategory
                  }
                  options={
                    placeCategoryOptions
                  }
                  placeholder={t(
                    "placeholders.category"
                  )}
                  onChange={
                    handleCategoryChange
                  }
                  styles={createSelectStyles(
                    Boolean(
                      invalidFields.category
                    )
                  )}
                  menuPortalTarget={
                    menuPortalTarget
                  }
                  menuPosition="fixed"
                  className="edit-place-react-select-container"
                  classNamePrefix="edit-place-react-select"
                  isClearable
                />

                <FieldError
                  show={
                    invalidFields.category
                  }
                  dropdown
                >
                  {t(
                    "errors.categoryRequired"
                  )}
                </FieldError>
              </div>

              <div className="edit-place-input-group">
                <label className="edit-place-label">
                  {t(
                    "fields.contactPhone"
                  )}
                  *
                </label>

                <div
                  className={`edit-place-phone-field ${
                    invalidFields.contactPhone
                      ? "has-error"
                      : ""
                  }`}
                >
                  <div className="edit-place-phone-country">
                    <Select
                      value={
                        selectedCountry
                      }
                      options={
                        countryOptions
                      }
                      onChange={
                        handleCountryPhoneChange
                      }
                      styles={
                        phoneSelectStyles
                      }
                      menuPortalTarget={
                        menuPortalTarget
                      }
                      menuPosition="fixed"
                      menuPlacement="auto"
                      className="edit-place-phone-select"
                      classNamePrefix="edit-place-phone-select"
                      isSearchable
                      formatOptionLabel={(
                        country,
                        {
                          context,
                        }
                      ) =>
                        context ===
                        "value" ? (
                          <div className="edit-place-phone-selected">
                            <img
                              src={`https://flagcdn.com/w40/${country.code}.png`}
                              alt={
                                country.label
                              }
                              className="edit-place-phone-flag"
                              onError={(
                                event
                              ) => {
                                event.currentTarget.style.display =
                                  "none";
                              }}
                            />

                            <span className="edit-place-phone-dial">
                              {
                                country.dialCode
                              }
                            </span>
                          </div>
                        ) : (
                          <div className="edit-place-phone-option">
                            <img
                              src={`https://flagcdn.com/w40/${country.code}.png`}
                              alt={
                                country.label
                              }
                              className="edit-place-phone-flag"
                              onError={(
                                event
                              ) => {
                                event.currentTarget.style.display =
                                  "none";
                              }}
                            />

                            <div className="edit-place-phone-option-text">
                              <span className="edit-place-phone-option-label">
                                {
                                  country.label
                                }
                              </span>

                              <span className="edit-place-phone-option-code">
                                {
                                  country.dialCode
                                }
                              </span>
                            </div>
                          </div>
                        )
                      }
                    />
                  </div>

                  <div className="edit-place-phone-number">
                    <input
                      type="tel"
                      value={
                        formData.contactPhone
                      }
                      placeholder={t(
                        "placeholders.phone"
                      )}
                      onChange={
                        handleContactPhoneChange
                      }
                    />
                  </div>
                </div>

                <FieldError
                  show={
                    invalidFields.contactPhone
                  }
                >
                  {t(
                    "errors.contactPhoneRequired"
                  )}
                </FieldError>
              </div>

              <div className="edit-place-input-group edit-place-input-group--full">
                <label
                  className="edit-place-label"
                  htmlFor="address"
                >
                  {t(
                    "fields.address"
                  )}
                  *
                </label>

                <input
                  type="text"
                  id="address"
                  name="address"
                  value={
                    formData.address
                  }
                  placeholder={t(
                    "placeholders.address"
                  )}
                  className={
                    invalidFields.address
                      ? "edit-place-input-error"
                      : ""
                  }
                  onChange={
                    handleChange
                  }
                />

                <FieldError
                  show={
                    invalidFields.address
                  }
                >
                  {t(
                    "errors.addressRequired"
                  )}
                </FieldError>
              </div>
            </div>
          </section>

          {/* =================================================
              Location
              ================================================= */}

          <section className="edit-place-section">
            <SectionHeading
              icon={
                LuMapPin
              }
              title={t(
                "sections.location"
              )}
              description={t(
                "sections.locationDescription"
              )}
            />

            <div className="edit-place-location-layout">
              <div className="edit-place-location-fields">
                <div className="edit-place-location-top-grid">
                  <div className="edit-place-input-group">
                    <label
                      className="edit-place-label"
                      htmlFor="postalCode"
                    >
                      {t(
                        "fields.postalCode"
                      )}
                      *
                    </label>

                    <input
                      type="text"
                      id="postalCode"
                      name="postalCode"
                      value={
                        formData.postalCode
                      }
                      placeholder={t(
                        "placeholders.postalCode"
                      )}
                      className={
                        invalidFields.postalCode
                          ? "edit-place-input-error"
                          : ""
                      }
                      onChange={
                        handleChange
                      }
                    />

                    <FieldError
                      show={
                        invalidFields.postalCode
                      }
                    >
                      {t(
                        "errors.postalCodeRequired"
                      )}
                    </FieldError>
                  </div>

                  <div className="edit-place-input-group">
                    <label
                      className="edit-place-label"
                      htmlFor="city"
                    >
                      {t(
                        "fields.city"
                      )}
                      *
                    </label>

                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={
                        formData.city
                      }
                      placeholder={t(
                        "placeholders.city"
                      )}
                      className={
                        invalidFields.city
                          ? "edit-place-input-error"
                          : ""
                      }
                      onChange={
                        handleChange
                      }
                    />

                    <FieldError
                      show={
                        invalidFields.city
                      }
                    >
                      {t(
                        "errors.cityRequired"
                      )}
                    </FieldError>
                  </div>

                  <div className="edit-place-input-group">
                    <label
                      className="edit-place-label"
                      htmlFor="country"
                    >
                      {t(
                        "fields.country"
                      )}
                      *
                    </label>

                    <Select
                      inputId="country"
                      value={
                        selectedPlaceCountry
                      }
                      options={
                        countryOptions
                      }
                      placeholder={t(
                        "placeholders.country"
                      )}
                      onChange={
                        handleCountryChange
                      }
                      styles={createSelectStyles(
                        Boolean(
                          invalidFields.country
                        )
                      )}
                      menuPortalTarget={
                        menuPortalTarget
                      }
                      menuPosition="fixed"
                      className="edit-place-react-select-container"
                      classNamePrefix="edit-place-react-select"
                      isClearable
                      formatOptionLabel={(
                        country
                      ) => (
                        <div className="edit-place-country-option">
                          <img
                            src={`https://flagcdn.com/w40/${country.code}.png`}
                            alt={
                              country.label
                            }
                            onError={(
                              event
                            ) => {
                              event.currentTarget.style.display =
                                "none";
                            }}
                          />

                          <span>
                            {
                              country.label
                            }
                          </span>
                        </div>
                      )}
                    />

                    <FieldError
                      show={
                        invalidFields.country
                      }
                      dropdown
                    >
                      {t(
                        "errors.countryRequired"
                      )}
                    </FieldError>
                  </div>
                </div>

                <div className="edit-place-location-bottom-grid">
                  <div className="edit-place-input-group">
                    <label
                      className="edit-place-label"
                      htmlFor="gMapsLink"
                    >
                      {t(
                        "fields.gMapsLink"
                      )}
                      *
                    </label>

                    <div className="edit-place-input-with-icon">
                      <LuLink2 className="edit-place-leading-icon" />

                      <input
                        type="text"
                        id="gMapsLink"
                        name="gMapsLink"
                        value={
                          formData.gMapsLink
                        }
                        placeholder={t(
                          "placeholders.gMapsLink"
                        )}
                        className={
                          invalidFields.gMapsLink
                            ? "edit-place-input-error"
                            : ""
                        }
                        onChange={
                          handleChange
                        }
                      />
                    </div>

                    <FieldError
                      show={
                        invalidFields.gMapsLink
                      }
                    >
                      {t(
                        "errors.gMapsLinkRequired"
                      )}
                    </FieldError>
                  </div>

                  <div className="edit-place-input-group">
                    <label
                      className="edit-place-label"
                      htmlFor="coordinates"
                    >
                      {t(
                        "fields.coordinates"
                      )}
                      *
                    </label>

                    <div className="edit-place-input-with-icon edit-place-input-with-icon--trailing">
                      <input
                        type="text"
                        id="coordinates"
                        name="coordinates"
                        value={
                          formData.coordinates
                        }
                        placeholder={t(
                          "placeholders.coordinates"
                        )}
                        className={
                          invalidFields.coordinates
                            ? "edit-place-input-error"
                            : ""
                        }
                        onChange={
                          handleChange
                        }
                      />

                      <LuLocateFixed className="edit-place-trailing-icon" />
                    </div>

                    <FieldError
                      show={
                        invalidFields.coordinates
                      }
                    >
                      {invalidFields.coordinates ===
                      "invalid"
                        ? t(
                            "errors.coordinatesInvalid"
                          )
                        : t(
                            "errors.coordinatesRequired"
                          )}
                    </FieldError>
                  </div>
                </div>
              </div>

              <EditPlaceMapPreview
                value={
                  formData.coordinates
                }
                emptyLabel={t(
                  "location.noCoordinates"
                )}
                invalidLabel={t(
                  "location.invalidCoordinates"
                )}
                previewLabel={t(
                  "location.preview"
                )}
                openMapLabel={t(
                  "location.openMap"
                )}
              />
            </div>
          </section>

          {/* =================================================
              Working hours
              ================================================= */}

          <section className="edit-place-section edit-place-section--working-hours">
            <div className="edit-place-workinghours-header">
              <SectionHeading
                icon={
                  LuClock3
                }
                title={t(
                  "workingHours.labelOptional"
                )}
                description={t(
                  "workingHours.description"
                )}
              />

              <button
                type="button"
                className={`edit-place-toggle-wh ${
                  showWorkingHours
                    ? "is-open"
                    : ""
                }`}
                aria-expanded={
                  showWorkingHours
                }
                onClick={() =>
                  setShowWorkingHours(
                    (
                      previous
                    ) =>
                      !previous
                  )
                }
              >
                <span>
                  {showWorkingHours
                    ? t(
                        "workingHours.hide"
                      )
                    : t(
                        "workingHours.show"
                      )}
                </span>

                {showWorkingHours ? (
                  <LuChevronUp />
                ) : (
                  <LuChevronDown />
                )}
              </button>
            </div>

            {showWorkingHours && (
              <div className="edit-place-wh-grid">
                  {DAYS.map(
                  (
                    day
                  ) => {
                    const intervals =
                      formData
                        .workingHours[
                        day
                      ] ||
                      [];

                    const dayHasError =
                      Boolean(
                        workingHoursErrors[
                          day
                        ]
                      );

                    return (
                      <article
                        key={
                          day
                        }
                        className={`edit-place-wh-card ${
                          dayHasError
                            ? "has-error"
                            : ""
                        }`}
                      >
                        <div className="edit-place-wh-card-head">
                          <span className="edit-place-wh-day-name">
                            {t(
                              `days.${day}`
                            )}
                          </span>
                        </div>

                        <div className="edit-place-wh-rows">
                          {intervals.map(
                            (
                              pair,
                              index
                            ) => (
                              <div
                                key={`${day}-${index}`}
                                className="edit-place-wh-row"
                              >
                                <TimePicker
                                  value={
                                    pair[0]
                                  }
                                  placeholder={t(
                                    "workingHours.timePlaceholder"
                                  )}
                                  isError={
                                    dayHasError
                                  }
                                  onChange={(
                                    value
                                  ) =>
                                    updateInterval(
                                      day,
                                      index,
                                      0,
                                      value
                                    )
                                  }
                                />

                                <span className="edit-place-wh-sep">
                                  –
                                </span>

                                <TimePicker
                                  value={
                                    pair[1]
                                  }
                                  placeholder={t(
                                    "workingHours.timePlaceholder"
                                  )}
                                  align="right"
                                  isError={
                                    dayHasError
                                  }
                                  onChange={(
                                    value
                                  ) =>
                                    updateInterval(
                                      day,
                                      index,
                                      1,
                                      value
                                    )
                                  }
                                />

                                <button
                                  type="button"
                                  className="edit-place-wh-remove"
                                  onClick={() =>
                                    removeInterval(
                                      day,
                                      index
                                    )
                                  }
                                  title={t(
                                    "workingHours.removeIntervalTitle"
                                  )}
                                  aria-label={t(
                                    "workingHours.removeIntervalAria",
                                    {
                                      index:
                                        index +
                                        1,

                                      day:
                                        t(
                                          `days.${day}`
                                        ),
                                    }
                                  )}
                                >
                                  <LuX />
                                </button>
                              </div>
                            )
                          )}
                        </div>

                        <button
                          type="button"
                          className="edit-place-wh-add"
                          onClick={() =>
                            addInterval(
                              day
                            )
                          }
                        >
                          <LuPlus />

                          <span>
                            {t(
                              "workingHours.addIntervalLabel"
                            )}
                          </span>
                        </button>

                        {intervals.length > 0 && (
                          <div className="edit-place-wh-apply-actions">
                            <button
                              type="button"
                              className="edit-place-wh-apply-button"
                              onClick={() =>
                                applyWorkingHoursToWeekdays(
                                  day
                                )
                              }
                            >
                              {t(
                                "workingHours.copySchedule.weekdays"
                              )}
                            </button>

                            <button
                              type="button"
                              className="edit-place-wh-apply-button edit-place-wh-apply-button--all"
                              onClick={() =>
                                applyWorkingHoursToAllDays(
                                  day
                                )
                              }
                            >
                              {t(
                                "workingHours.copySchedule.allDays"
                              )}
                            </button>
                          </div>
                        )}

                        <div className="edit-place-wh-error">
                          {workingHoursErrors[
                            day
                          ] ||
                            ""}
                        </div>
                      </article>
                    );
                  }
                  )}
              </div>
            )}
          </section>

          {/* =================================================
              Actions
              ================================================= */}

          <div className="edit-place-button-row">
            <button
              type="button"
              className="edit-place-cancel-button"
              onClick={
                handleCancel
              }
              disabled={
                isSubmitting
              }
            >
              {t(
                "buttons.cancel"
              )}
            </button>

            <button
              type="submit"
              className="edit-place-submit-button"
              disabled={
                isSubmitting
              }
            >
              {isSubmitting
                ? t(
                    "buttons.saving"
                  )
                : t(
                    "buttons.saveChanges"
                  )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default EditPlace;